import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_STYLES = {
  ON_TRACK: { bg: 'rgba(16,185,129,0.15)', color: '#10B981', label: 'On Track' },
  DELAYED: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444', label: 'Delayed' },
  AHEAD: { bg: 'rgba(59,130,246,0.15)', color: '#3B82F6', label: 'Ahead' },
  COMPLETED: { bg: 'rgba(16,185,129,0.2)', color: '#059669', label: 'Completed' },
  PO_PENDING: { bg: 'rgba(196,168,255,0.15)', color: '#C4A8FF', label: 'PO Pending' },
  IN_TRANSIT: { bg: 'rgba(56,189,248,0.15)', color: '#38BDF8', label: 'In Transit' },
  IN_PROGRESS: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B', label: 'In Progress' },
  NOT_STARTED: { bg: 'var(--badge-muted-bg, rgba(100,116,139,0.15))', color: 'var(--text-disabled, #64748b)', label: 'Not Started' },
};

function StatusCellRenderer(params) {
  const status = params.value;
  if (!status) return <span style={{ color: 'var(--text-disabled)' }}>-</span>;
  const s = STATUS_STYLES[status] || STATUS_STYLES.NOT_STARTED;
  return (
    <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, backgroundColor: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function ProgressCellRenderer(params) {
  const value = params.value ?? 0;
  const color = value === 100 ? '#10B981' : value > 70 ? '#3B82F6' : value > 30 ? '#F59E0B' : value > 0 ? '#EF4444' : 'var(--text-disabled)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
      <div style={{ flex: 1, height: '5px', backgroundColor: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(value, 100)}%`, height: '100%', backgroundColor: color, borderRadius: '3px', transition: 'width 0.3s ease' }} />
      </div>
      <span style={{ fontSize: '10px', color, fontWeight: 600, minWidth: '30px', textAlign: 'right' }}>
        {value.toFixed(1)}%
      </span>
    </div>
  );
}

function CategoryCellRenderer(params) {
  if (!params.value) return null;
  const isBulk = params.value === 'BULK';
  return (
    <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
      backgroundColor: isBulk ? 'rgba(94,234,212,0.15)' : 'rgba(196,168,255,0.15)',
      color: isBulk ? '#5EEAD4' : '#C4A8FF' }}>
      {params.value}
    </span>
  );
}

function PhaseCellRenderer(params) {
  if (!params.value) return null;
  const isPre = params.value === 'PRE_ORDER';
  return (
    <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
      backgroundColor: isPre ? 'rgba(94,234,212,0.15)' : 'rgba(196,168,255,0.15)',
      color: isPre ? '#5EEAD4' : '#C4A8FF' }}>
      {params.value?.replace('_', '-')}
    </span>
  );
}

function DateCellRenderer(params) {
  if (!params.value) return <span style={{ color: 'var(--text-disabled)' }}>-</span>;
  const date = new Date(params.value);
  const formatted = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  const colId = params.column?.getColId() || '';
  let color = 'var(--text-secondary, #94a3b8)';
  if (colId.includes('_actual')) color = '#10B981';
  else if (colId.includes('_forecast')) color = '#F59E0B';
  return <span style={{ color, fontSize: '11px' }}>{formatted}</span>;
}

function GateStatusRenderer(params) {
  const status = params.value;
  if (!status) return <span style={{ color: 'var(--text-disabled)' }}>-</span>;
  const s = STATUS_STYLES[status] || STATUS_STYLES.NOT_STARTED;
  return (
    <span style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '9px', fontWeight: 600, backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function DateEditor(props) {
  const [value, setValue] = useState(props.value ? props.value.split('T')[0] : '');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const handleChange = (e) => {
    const newVal = e.target.value;
    setValue(newVal);
    if (props.onValueChange) props.onValueChange(newVal ? new Date(newVal).toISOString() : null);
  };
  return (
    <input ref={inputRef} type="date" value={value} onChange={handleChange}
      style={{ width: '100%', backgroundColor: 'var(--input-bg)', color: 'var(--text-primary)',
        border: '1px solid var(--accent-primary, #3B82F6)', borderRadius: '4px', padding: '4px 6px', fontSize: '11px', outline: 'none' }} />
  );
}

export default function MCIMatrixGrid({ entries, projectId, onRefresh, isDark, stageGateConfigs }) {
  const gridRef = useRef(null);

  // Build row data: flatten pre/post order stage gates into flat columns
  const rowData = useMemo(() => {
    if (!entries) return [];
    return entries.map((entry) => {
      const row = {
        id: entry.id,
        mciLineNumber: entry.mciLineNumber,
        materialDescription: entry.materialDescription,
        materialCategory: entry.materialCategory,
        materialTypeName: entry.materialTypeName,
        discipline: entry.discipline,
        area: entry.area || '',
        tagNumber: entry.tagNumber || '',
        bulkItemCode: entry.bulkItemCode || '',
        mrNumber: entry.mrNumber || '',
        poNumber: entry.poNumber || '',
        vendor: entry.vendor || '',
        currentPhase: entry.currentPhase,
        overallProgress: entry.overallProgress || 0,
        overallStatus: entry.overallStatus,
        preOrderProgressPercent: entry.preOrderProgressPercent || 0,
        postOrderProgressPercent: entry.postOrderProgressPercent || 0,
        _raw: entry,
      };

      // Flatten pre-order gates
      (entry.preOrderProgress || []).forEach((sg, i) => {
        const p = `pre_${i}`;
        row[`${p}_name`] = sg.stageGateName;
        row[`${p}_planned`] = sg.plannedDate;
        row[`${p}_forecast`] = sg.forecastDate;
        row[`${p}_actual`] = sg.actualDate;
        row[`${p}_status`] = sg.status;
        row[`${p}_weight`] = sg.weightagePercent;
        row[`${p}_gateId`] = sg.stageGateId;
      });

      // Flatten post-order gates
      (entry.postOrderProgress || []).forEach((sg, i) => {
        const p = `post_${i}`;
        row[`${p}_name`] = sg.stageGateName;
        row[`${p}_planned`] = sg.plannedDate;
        row[`${p}_forecast`] = sg.forecastDate;
        row[`${p}_actual`] = sg.actualDate;
        row[`${p}_status`] = sg.status;
        row[`${p}_weight`] = sg.weightagePercent;
        row[`${p}_gateId`] = sg.stageGateId;
      });

      return row;
    });
  }, [entries]);

  // Derive stage gate columns from first entry that has them, or from configs
  const { preGates, postGates } = useMemo(() => {
    const pre = [];
    const post = [];
    // Get gate structure from entries
    for (const entry of (entries || [])) {
      if (entry.preOrderProgress?.length > pre.length) {
        entry.preOrderProgress.forEach((sg, i) => { pre[i] = { idx: i, name: sg.stageGateName, weight: sg.weightagePercent }; });
      }
      if (entry.postOrderProgress?.length > post.length) {
        entry.postOrderProgress.forEach((sg, i) => { post[i] = { idx: i, name: sg.stageGateName, weight: sg.weightagePercent }; });
      }
    }
    return { preGates: pre, postGates: post };
  }, [entries]);

  const handleCellValueChanged = useCallback(async (params) => {
    const { data, colDef, newValue } = params;
    const field = colDef.field;
    if (!field || !data._raw) return;

    // Parse field: pre_0_planned, post_2_actual, etc.
    const match = field.match(/^(pre|post)_(\d+)_(planned|forecast|actual)$/);
    if (!match) return;

    const phase = match[1];
    const gateIdx = parseInt(match[2]);
    const dateType = match[3];

    // Find the entry and gate
    const entry = data._raw;
    const gates = phase === 'pre' ? entry.preOrderProgress : entry.postOrderProgress;
    if (!gates || !gates[gateIdx]) return;

    const gate = gates[gateIdx];
    const updateField = dateType + 'Date';
    const routePhase = phase === 'pre' ? 'pre-order' : 'post-order';

    try {
      await api.put(`/v1/projects/${projectId}/mci/entries/${entry.id}/${routePhase}/${gate.stageGateId}`, {
        [updateField]: newValue || null
      });
      toast.success(`Updated ${gate.stageGateName} ${dateType} date`);
      if (onRefresh) onRefresh();
      window.dispatchEvent(new CustomEvent('mci-register-updated', { detail: { source: 'matrix-grid' } }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update');
    }
  }, [projectId, onRefresh]);

  const columnDefs = useMemo(() => {
    const fixedCols = [
      { headerName: '#', valueGetter: (p) => p.node.rowIndex + 1, width: 45, pinned: 'left', sortable: false, filter: false },
      { headerName: 'MCI#', field: 'mciLineNumber', pinned: 'left', width: 95, filter: 'agTextColumnFilter',
        cellStyle: { color: 'var(--accent-primary, #3B82F6)', fontWeight: 500, fontSize: '11px', fontFamily: 'monospace' } },
      { headerName: 'Description', field: 'materialDescription', pinned: 'left', minWidth: 180, width: 220, filter: 'agTextColumnFilter',
        tooltipField: 'materialDescription', cellStyle: { fontSize: '11px' } },
      { headerName: 'Cat', field: 'materialCategory', width: 72, cellRenderer: CategoryCellRenderer },
      { headerName: 'Type', field: 'materialTypeName', width: 100, filter: 'agTextColumnFilter', cellStyle: { fontSize: '11px' } },
      { headerName: 'Disc', field: 'discipline', width: 85, filter: 'agTextColumnFilter', cellStyle: { fontSize: '11px' } },
      { headerName: 'MR No.', field: 'mrNumber', width: 80, cellStyle: { fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-muted)' } },
      { headerName: 'PO No.', field: 'poNumber', width: 80, cellStyle: { fontSize: '11px', fontFamily: 'monospace' } },
      { headerName: 'Phase', field: 'currentPhase', width: 90, cellRenderer: PhaseCellRenderer },
    ];

    // Pre-Order gate columns
    const preCols = preGates.length > 0 ? [{
      headerName: 'Pre-Order Gates',
      headerClass: 'ag-header-group-pre-order',
      children: preGates.map(g => ({
        headerName: `${g.name} (${g.weight}%)`,
        children: [
          { headerName: 'Plan', field: `pre_${g.idx}_planned`, width: 95, cellRenderer: DateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'Fcst', field: `pre_${g.idx}_forecast`, width: 95, cellRenderer: DateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'Actual', field: `pre_${g.idx}_actual`, width: 95, cellRenderer: DateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'St', field: `pre_${g.idx}_status`, width: 72, cellRenderer: GateStatusRenderer },
        ]
      }))
    }] : [];

    // Post-Order gate columns
    const postCols = postGates.length > 0 ? [{
      headerName: 'Post-Order Gates',
      headerClass: 'ag-header-group-post-order',
      children: postGates.map(g => ({
        headerName: `${g.name} (${g.weight}%)`,
        children: [
          { headerName: 'Plan', field: `post_${g.idx}_planned`, width: 95, cellRenderer: DateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'Fcst', field: `post_${g.idx}_forecast`, width: 95, cellRenderer: DateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'Actual', field: `post_${g.idx}_actual`, width: 95, cellRenderer: DateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'St', field: `post_${g.idx}_status`, width: 72, cellRenderer: GateStatusRenderer },
        ]
      }))
    }] : [];

    const summaryCols = [
      { headerName: 'Progress', field: 'overallProgress', width: 130, cellRenderer: ProgressCellRenderer, sortable: true },
      { headerName: 'Status', field: 'overallStatus', width: 100, cellRenderer: StatusCellRenderer, sortable: true },
    ];

    return [...fixedCols, ...preCols, ...postCols, ...summaryCols];
  }, [preGates, postGates]);

  const defaultColDef = useMemo(() => ({ sortable: true, resizable: true, suppressMovable: false, editable: false }), []);
  const gridTheme = isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';

  return (
    <div className={gridTheme} style={{ width: '100%', height: '600px' }} data-testid="mci-matrix-grid">
      <AgGridReact ref={gridRef} theme="legacy" rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
        onCellValueChanged={handleCellValueChanged} animateRows={true} rowSelection="multiple"
        suppressRowClickSelection={true} enableCellTextSelection={true} tooltipShowDelay={500}
        getRowId={(params) => params.data.id}
        overlayNoRowsTemplate='<span style="color: var(--text-muted); font-size: 13px;">No MCI entries found.</span>' />
    </div>
  );
}
